"use client";

import React, { useState } from "react";
import {
  BLOG_HATCH,
  deriveMark,
  tintFor,
} from "@/src/features/blog/blogVisuals";

interface IBlogCoverArtProps {
  /** Slug or id — keeps the tint stable for a given blog everywhere. */
  seed: string;
  /** Blog title — the decorative serif "mark" is derived from it. */
  title: string;
  /** Optional real banner image; overlays the generated cover when present. */
  imageUrl?: string | null;
  /** Small mono eyebrow at the top-left; omit for compact covers. */
  eyebrow?: string;
  /** Tailwind classes for the outer box (aspect ratio, rounding, etc.). */
  className?: string;
  /** Inner padding (Tailwind). */
  padding?: string;
  /** Tailwind class controlling the mark font size. */
  markClassName?: string;
  /**
   * Visibility wrapper for the image — e.g. "md:hidden" to show the image on
   * mobile only (used by the article hero). Defaults to always visible.
   */
  imageVisibilityClassName?: string;
}

function BlogCoverArt({
  seed,
  title,
  imageUrl,
  eyebrow,
  className = "",
  padding = "p-5",
  markClassName = "text-44",
  imageVisibilityClassName = "",
}: IBlogCoverArtProps) {
  const { tint, ink } = tintFor(seed);
  const mark = deriveMark(title);
  const [imgFailed, setImgFailed] = useState(false);
  const showImage = !!imageUrl && !imgFailed;

  return (
    <div
      className={`relative overflow-hidden ${className}`}
      style={{ background: tint }}
    >
      {/* Generated cover: mono eyebrow + big serif mark */}
      <div
        className={`absolute inset-0 flex flex-col ${
          eyebrow ? "justify-between" : "justify-end"
        } ${padding}`}
      >
        {eyebrow ? (
          <div
            className="font-mono-blog text-10 tracking-[0.14em] opacity-60"
            style={{ color: ink }}
          >
            {eyebrow}
          </div>
        ) : null}
        <div
          className={`font-serif-blog leading-none tracking-[-0.03em] opacity-90 ${markClassName}`}
          style={{ color: ink }}
        >
          {mark}
        </div>
      </div>

      {/* Diagonal hatch texture */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: BLOG_HATCH }}
      />

      {/* Real image overlays the cover when available */}
      {showImage ? (
        <div className={`absolute inset-0 ${imageVisibilityClassName}`}>
          {/* Blurred copy fills the letterbox so the real image shows uncropped. */}
          <img
            src={imageUrl as string}
            alt=""
            aria-hidden
            className="absolute inset-0 h-full w-full scale-110 object-cover blur-xl"
          />
          <img
            src={imageUrl as string}
            alt={title}
            onError={() => setImgFailed(true)}
            className="absolute inset-0 h-full w-full object-contain transition-transform duration-700 ease-out group-hover:scale-[1.04]"
          />
        </div>
      ) : null}
    </div>
  );
}

export default BlogCoverArt;
